/* TCC enhancements for the Judge.me all-reviews page.
 * Two responsibilities:
 *   1. Inject an AI-style customer-summary block between the stars
 *      block and the customer photos gallery.
 *   2. Convert the photos gallery into a horizontal carousel with
 *      arrow controls and a 5-second auto-advance (paused on hover).
 *
 * Loaded via conditional <script> in layout/theme.liquid only on
 * pages whose template suffix is judgeme_reviews.
 */

(function () {
  'use strict';

  const AUTOSCROLL_MS = 3000;

  /* Edit this string to change the on-page summary. The summary doesn't
   * auto-update; tweaking the text here is the intended workflow.
   */
  const AI_SUMMARY_LABEL = 'What customers say';
  const AI_SUMMARY_TEXT = (
    'Customers love the fast UK delivery and careful packaging. '
    + 'Reviewers often mention the handwritten thank-you notes and '
    + 'freebie cards Alex adds to every order. Many say they keep '
    + 'coming back because Alex really knows the hobby and answers '
    + 'questions clearly — a level of personal customer service '
    + 'you don\'t get from larger stores.'
  );

  function waitFor(selector, callback, maxMs) {
    maxMs = maxMs || 12000;
    const start = Date.now();
    const tick = function () {
      const el = document.querySelector(selector);
      if (el) return callback(el);
      if (Date.now() - start > maxMs) return;
      setTimeout(tick, 120);
    };
    tick();
  }

  function injectAISummary() {
    if (document.querySelector('.tcc-ai-summary')) return;
    /* Insert as sibling AFTER the stars header — that's the widely-styled
       block, so we inherit its width context. Falling back to before the
       gallery if the header isn't found. */
    const header = document.querySelector('.jdgm-all-reviews__header');
    const gallery = document.querySelector('.jdgm-gallery-wrapper');
    if (!header && !gallery) return;

    const summary = document.createElement('div');
    summary.className = 'tcc-ai-summary';
    summary.setAttribute('role', 'note');

    const label = document.createElement('div');
    label.className = 'tcc-ai-summary__label';
    label.textContent = AI_SUMMARY_LABEL;

    const para = document.createElement('p');
    para.className = 'tcc-ai-summary__text';
    para.textContent = AI_SUMMARY_TEXT;

    summary.appendChild(label);
    summary.appendChild(para);

    if (header && header.parentElement) {
      header.parentElement.insertBefore(summary, header.nextSibling);
    } else {
      gallery.parentElement.insertBefore(summary, gallery);
    }
  }

  function setupCarousel() {
    const gallery = document.querySelector('.jdgm-all-reviews-widget .jdgm-gallery');
    if (!gallery) return;
    if (gallery.dataset.tccCarouselSetup === '1') return;
    gallery.dataset.tccCarouselSetup = '1';

    /* Hide any 'See more' tile in the gallery — the text-content match
       catches it regardless of which Judge.me class it actually uses. */
    /* Identify the See more tile: real customer photos have an
       <img data-review-id="..."> inside (set by Judge.me when the
       photo is tied to a specific review). The See more placeholder
       has either no img or an img without that attribute, so
       checking for data-review-id reliably distinguishes them. */
    function hideNonPhotoChildren() {
      Array.prototype.forEach.call(gallery.children, function (child) {
        const img = child.querySelector('img');
        if (!img || !img.hasAttribute('data-review-id')) {
          child.style.display = 'none';
          return;
        }
        // Detect the See more tile: its wrapper has a non-trivial ::before
        // pseudo-content. Desktop renders 'See more'; mobile sometimes
        // renders '…' or '...'. Real photo wrappers have ::before of 'none'
        // or empty string, so anything else means see-more in some form.
        const wrapper = child.querySelector('.jdgm-gallery__thumbnail-wrapper');
        if (wrapper) {
          try {
            const raw = getComputedStyle(wrapper, '::before').content || '';
            // Strip the quotes CSS wraps content strings in, then trim.
            const text = raw.replace(/^["']|["']$/g, '').trim();
            if (text && text !== 'none' && (/see\s*more|view\s*all|more\s*reviews/i.test(text) || /^[\.…]+$/.test(text))) {
              child.setAttribute('data-tcc-see-more', 'true');
            }
          } catch (e) { /* ignore */ }
        }
      });
    }
    hideNonPhotoChildren();
    // Re-run shortly after init to catch pseudo-elements that may be
    // applied late on mobile (where viewport-driven CSS can re-fire).
    setTimeout(hideNonPhotoChildren, 600);
    setTimeout(hideNonPhotoChildren, 1500);
    setTimeout(hideNonPhotoChildren, 3000);

    /* Force-load every gallery image. Judge.me lazy-loads: src is empty
       (or stale) until its IntersectionObserver fires. Items outside the
       initial viewport stay black until something scrolls them into view.
       Copying data-src → src and removing the loading marker triggers
       browser image fetch immediately. */
    function forceLoadImages() {
      gallery.querySelectorAll('img[data-src]').forEach(function (img) {
        const target = img.getAttribute('data-src');
        if (target && img.getAttribute('src') !== target) {
          img.setAttribute('src', target);
        }
        const w = img.closest('.jdgm-gallery__thumbnail-wrapper');
        if (w) w.classList.remove('jdgm--loading');
      });
    }
    forceLoadImages();

    /* Re-run when Judge.me adds children later (paginate / lazy load). */
    if (typeof MutationObserver !== 'undefined') {
      const mo = new MutationObserver(function () {
        hideNonPhotoChildren();
        forceLoadImages();
      });
      mo.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-src'] });
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'tcc-gallery-carousel';

    const left = document.createElement('button');
    left.type = 'button';
    left.className = 'tcc-gallery-carousel__arrow tcc-gallery-carousel__arrow--left';
    left.setAttribute('aria-label', 'Previous customer photos');
    left.innerHTML = '‹';

    const right = document.createElement('button');
    right.type = 'button';
    right.className = 'tcc-gallery-carousel__arrow tcc-gallery-carousel__arrow--right';
    right.setAttribute('aria-label', 'Next customer photos');
    right.innerHTML = '›';

    const viewport = document.createElement('div');
    viewport.className = 'tcc-gallery-carousel__viewport';

    const galleryParent = gallery.parentElement;
    galleryParent.insertBefore(wrapper, gallery);
    wrapper.appendChild(left);
    wrapper.appendChild(viewport);
    wrapper.appendChild(right);
    viewport.appendChild(gallery);

    function step(direction) {
      const first = gallery.querySelector('.jdgm-gallery__thumbnail-link:not([data-tcc-see-more])');
      const itemWidth = (first ? first.offsetWidth : 160) + 8;
      const max = Math.max(0, gallery.scrollWidth - gallery.clientWidth);
      // No overflow at all — nothing to scroll.
      if (max <= 1) return;
      const current = gallery.scrollLeft;
      // Use a forgiving threshold (half an item width) so smooth-scroll's
      // pixel-imprecise stops still register as 'at the edge' and wrap.
      const edgeTol = Math.max(8, itemWidth / 2);
      let target;
      if (direction > 0 && current >= max - edgeTol) {
        target = 0;
      } else if (direction < 0 && current <= edgeTol) {
        target = max;
      } else {
        target = Math.max(0, Math.min(max, current + direction * itemWidth));
      }
      gallery.scrollTo({ left: target, behavior: 'smooth' });
    }

    left.addEventListener('click', function () { step(-1); });
    right.addEventListener('click', function () { step(1); });

    let timer = null;
    function startTimer() {
      stopTimer();
      timer = setInterval(function () { step(1); }, AUTOSCROLL_MS);
    }
    function stopTimer() {
      if (timer) clearInterval(timer);
      timer = null;
    }
    wrapper.addEventListener('mouseenter', stopTimer);
    wrapper.addEventListener('mouseleave', startTimer);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopTimer(); else startTimer();
    });
    startTimer();
  }

  function init() {
    injectAISummary();
    setupCarousel();
  }

  /* Judge.me's widget renders asynchronously, so wait for the all-reviews
   * widget root to appear before running our enhancements. */
  waitFor('.jdgm-all-reviews-widget', function () {
    /* Small extra delay so Judge.me has finished setting up child elements
     * (gallery, subtab, etc.) before we wrap them. */
    setTimeout(init, 400);
  });
})();
